import { Dispatch, SetStateAction } from "react";
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid2,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { EntryItem, GroupFilterSelected, GroupFilterType } from "./types/GroupFilters.type";
import {
  genInitialEntries,
  handleFilterCheckValChange,
  handleFilterQtyChange,
  initFilterSubItemStyles,
  initFilterSubStyles
} from "./functions/GroupFilterSub.functions";
import { BookingFilterMode } from "../Domain/booking-filter-mode-enum.type";

type P = {
  type: GroupFilterType;
  toggleMoreItems: boolean;
  entries: EntryItem[] | undefined;
  showDetailGroupFilter: boolean;
  filterName: string;
  filterItems: GroupFilterSelected[];
  filterMode: BookingFilterMode;
  isCapacityReportUsed: boolean;
  showQuantity?: boolean;
  stackFilterItems?: boolean;
  showEmptySection?: boolean;
  setToggleMoreItems: (t: boolean) => void;
  setFilterItems: Dispatch<SetStateAction<GroupFilterSelected[]>>;
};

type FilterEntryCheckboxProps = {
  entry: EntryItem;
  type: GroupFilterType;
  filterItems: GroupFilterSelected[];
  filterMode: BookingFilterMode;
  setFilterItems: Dispatch<SetStateAction<GroupFilterSelected[]>>;
};

function StackedFilterCheckbox({
  entry,
  type,
  filterItems,
  filterMode,
  setFilterItems
}: FilterEntryCheckboxProps) {
  const { initialDefaultChecked } = genInitialEntries(filterItems, entry, type, filterMode);

  return (
    <FormControlLabel
      sx={{ m: 0, mr: 2, flex: "0 0 auto" }}
      control={
        <Checkbox
          data-testid={`${type}-booking-group-filter-checkBox`}
          checked={Boolean(initialDefaultChecked)}
          name={entry.name}
          id={entry.id.toString()}
          onChange={e => handleFilterCheckValChange(e, type, filterItems, setFilterItems)}
        />
      }
      label={<Typography variant="body2">{entry.name}</Typography>}
    />
  );
}

export const GroupFiltersSub: React.FC<P> = props => {
  const {
    type,
    toggleMoreItems,
    entries,
    showDetailGroupFilter,
    filterName,
    filterItems,
    filterMode,
    isCapacityReportUsed,
    showQuantity = true,
    stackFilterItems = false,
    showEmptySection = false,
    setToggleMoreItems,
    setFilterItems
  } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const filterSubStyles = initFilterSubStyles(
    type,
    showDetailGroupFilter,
    toggleMoreItems,
    isMobile,
    isCapacityReportUsed
  );
  const filterSubItemStyles = initFilterSubItemStyles(type, showDetailGroupFilter, isMobile);
  const entryList = entries ?? [];
  const filteredEntries = entryList.filter(en => en.name.toLowerCase().includes(filterName));
  const FilterBodyContainer = stackFilterItems ? Box : Grid2;
  const filterBodySx = stackFilterItems
    ? {
        pl: 0.5,
        display: "flex",
        flexDirection: "column",
        width: "100%"
      }
    : { pl: filterSubStyles.filterBodyPaddingLeft };

  if (entryList.length === 0 && !showEmptySection) return null;

  return (
    <Grid2
      width={"100%"}
      display={"block"}
      data-testid="booking-group-equipment-categories-filter-container"
      sx={{ display: "block", mb: 2 }}
    >
      {/* header type of group filter item */}
      <Grid2
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.5,
          ...(stackFilterItems ? { pl: 0.5 } : {})
        }}
        data-testid="booking-group-equipment-categories-filter-header"
      >
        {!stackFilterItems && (
          <IconButton
            sx={{
              transform: filterSubStyles.expandIconTransform,
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.shortest
              })
            }}
            data-testid="toggle-more-items-btn"
            onClick={() => setToggleMoreItems(!toggleMoreItems)}
          >
            <ExpandMore />
          </IconButton>
        )}
        <Typography
          variant={stackFilterItems ? "subtitle2" : "subtitle1"}
          color="text.primary"
          sx={{
            fontSize: stackFilterItems ? undefined : filterSubStyles.titleTypoFontSize,
            fontWeight: 600
          }}
        >
          {t(type)}
        </Typography>
      </Grid2>
      {/* list of group filter item */}
      <FilterBodyContainer
        sx={filterBodySx}
        data-testid="booking-group-equipment-categories-filter-body"
      >
        <Collapse
          in={!toggleMoreItems}
          timeout="auto"
          unmountOnExit
          data-testid={`${type}-booking-group-filter-collapse`}
        >
          {filteredEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              {t("_booking_earlyInventoryFilter_noOptions")}
            </Typography>
          ) : stackFilterItems ? (
            <FormGroup
              row
              sx={{
                gap: 1,
                width: "100%",
                flexWrap: "wrap",
                alignItems: "flex-start"
              }}
            >
              {filteredEntries.map(entry => (
                <StackedFilterCheckbox
                  key={entry.id}
                  entry={entry}
                  type={type}
                  filterItems={filterItems}
                  filterMode={filterMode}
                  setFilterItems={setFilterItems}
                />
              ))}
            </FormGroup>
          ) : (
            filteredEntries.map(entry => {
              const { initialDefaultChecked, initialDefaultQuantity } = genInitialEntries(
                filterItems,
                entry,
                type,
                filterMode
              );

              return (
                <FormGroup key={entry.id}>
                  <Grid2
                    container
                    data-testid={`${type}-booking-group-filter-container`}
                    sx={[showDetailGroupFilter ? { my: 0.75 } : { my: 0.25 }]}
                  >
                    <Grid2 size={{ xs: filterSubStyles.controlLabelItemXs }}>
                      <FormControlLabel
                        sx={[
                          { minWidth: filterSubStyles.controlLabelMinWidth },
                          isMobile ? { mr: 0 } : { mr: 2 }
                        ]}
                        control={
                          <Checkbox
                            data-testid={`${type}-booking-group-filter-checkBox`}
                            checked={Boolean(initialDefaultChecked)}
                            name={entry.name}
                            id={entry.id.toString()}
                            onChange={e =>
                              handleFilterCheckValChange(e, type, filterItems, setFilterItems)
                            }
                          />
                        }
                        slotProps={{ typography: { sx: { width: "100%" } } }}
                        label={
                          <Grid2
                            container
                            sx={{ alignItems: "center", overflowWrap: "anywhere" }}
                            data-testid={`${type}-booking-group-filter-checkBox-label-container`}
                          >
                            <Grid2 size={{ xs: filterSubItemStyles.entryNameXs }}>
                              <Typography>{entry.name}</Typography>
                            </Grid2>

                            {!isMobile && (
                              <Grid2
                                size={{ xs: filterSubItemStyles.descItemXs }}
                                data-testid={`${type}-booking-group-filter-entry-item-description`}
                                sx={{
                                  overflowWrap: "anywhere",
                                  pl: filterSubItemStyles.descItemPaddingLeft
                                }}
                              >
                                {showDetailGroupFilter && (
                                  <Typography>{entry.description}</Typography>
                                )}
                              </Grid2>
                            )}
                          </Grid2>
                        }
                      />
                    </Grid2>

                    {showQuantity && type === GroupFilterType.EQUIPMENTCATEGORY && (
                      <Grid2
                        size={{ xs: filterSubItemStyles.quantityFieldXs }}
                        data-testid={`${type}-booking-group-filter-entry-item-field`}
                      >
                        <TextField
                          data-testid={`${type}-booking-group-filter-textField`}
                          sx={{ width: filterSubItemStyles.quantityFieldWidth }}
                          type={"number"}
                          name={entry.name}
                          id={entry.id.toString()}
                          defaultValue={initialDefaultQuantity}
                          slotProps={{ input: { min: 0, style: { paddingLeft: "10px" } } }}
                          onChange={e => handleFilterQtyChange(e, setFilterItems)}
                        />
                      </Grid2>
                    )}
                  </Grid2>
                  {isMobile && (
                    <Grid2
                      container
                      data-testid={`${type}-booking-group-filter-item-desc-cont`}
                      sx={{ flexDirection: "column", ml: 4, width: "auto" }}
                    >
                      <Grid2
                        size={{ xs: filterSubStyles.descItemXsMobile }}
                        data-testid={`${type}-booking-group-filter-entry-item-description`}
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {showDetailGroupFilter && <Typography>{entry.description}</Typography>}
                      </Grid2>
                    </Grid2>
                  )}
                  {showDetailGroupFilter && (
                    <Divider
                      sx={{
                        width: filterSubStyles.dividerWidth,
                        m: 1,
                        opacity: "0.6"
                      }}
                    />
                  )}
                </FormGroup>
              );
            })
          )}
        </Collapse>
      </FilterBodyContainer>
    </Grid2>
  );
};
